import { TrashIcon } from '@heroicons/react/24/outline';
import {
  FormControl,
  FormHelperText,
  IconButton,
  Input,
  LinearProgress,
  Textarea,
} from '@mui/joy';

import { useMemo } from 'react';
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldErrors,
  useFieldArray,
} from 'react-hook-form';

import { getFeedbackConfigSubtitle } from '@/Pages/AnnotationQueue/utils/getFeedbackConfigSubtitle';
import { useFeedbackConfigs } from '@/hooks/useSwr';
import {
  AnnotationQueueRubricItem,
  FeedbackConfigSchema,
} from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { FeedbackChip } from '../FeedbackChips';
import {
  AddFeedbackPopoverWithButton,
  AnnotationQueueFormInput,
} from './AnnotationQueueCrudPane';

const INPUT_SX = {
  background: 'transparent',
  p: 1,
  px: 2,
  borderRadius: 0,
  lineHeight: '1em',
  minHeight: 'auto',
  border: 'none',
  ':before': { content: 'none' },
};

export const AnnotationQueueRubricCrud = ({
  control,
  feedbackRubricInstructions,
  onChangeInstructions,
  feedbackRubricItems,
  focusOn,
  errors,
  isEvaluatorMode,
}: {
  control: Control<AnnotationQueueFormInput, any, AnnotationQueueFormInput>;
  errors: FieldErrors<AnnotationQueueFormInput>;
  feedbackRubricInstructions?: string;
  feedbackRubricItems?: AnnotationQueueRubricItem[];
  onChangeInstructions: (instructions: string) => void;
  focusOn?: 'instructions' | 'feedback';
  isEvaluatorMode?: boolean;
}) => {
  const feedbackConfigs = useFeedbackConfigs({
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'feedbackRubricItems',
  });

  const filteredFeedbackConfigs = useMemo(() => {
    if (feedbackConfigs.data) {
      return feedbackConfigs.data.filter((config) => {
        const isAlreadySelected = feedbackRubricItems?.some(
          (field) => field.feedback_key === config.feedback_key
        );
        return !isAlreadySelected;
      });
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackConfigs.data, JSON.stringify(feedbackRubricItems)]);

  const rubricItemsRender = () => {
    if (filteredFeedbackConfigs || isEvaluatorMode) {
      if (fields.length > 0) {
        return fields.map((field, index) => {
          return (
            <FormControl
              error={!!errors.feedbackRubricItems?.[index]}
              key={field.id}
            >
              <Controller
                name={`feedbackRubricItems.${index}`}
                control={control}
                key={field.id}
                rules={{
                  validate: (value) => {
                    if (!value.feedback_key) {
                      return 'Feedback key is required';
                    }
                    return true;
                  },
                }}
                render={({ field }) => {
                  const selectedFeedbackConfig = feedbackConfigs.data?.find(
                    (config) => config.feedback_key === field.value.feedback_key
                  );
                  return selectedFeedbackConfig || isEvaluatorMode ? (
                    <RubricItemEditor
                      field={field}
                      error={errors.feedbackRubricItems?.[index]?.message}
                      selectedFeedbackConfig={
                        isEvaluatorMode || !selectedFeedbackConfig
                          ? {
                              feedback_key: field.value.feedback_key!,
                              feedback_config: {
                                type: 'categorical',
                                categories: Object.entries(
                                  field.value.score_descriptions ?? {}
                                )
                                  .map(([key, _value]) => {
                                    const numValue = parseInt(key);
                                    return isNaN(numValue)
                                      ? null
                                      : { value: numValue };
                                  })
                                  .filter((value) => value !== null) as {
                                  value: number;
                                }[],
                              },
                            }
                          : selectedFeedbackConfig
                      }
                      remove={() => remove(index)}
                      isEvaluatorMode={isEvaluatorMode}
                    />
                  ) : (
                    <div></div>
                  ); // Should never happen
                }}
              />
            </FormControl>
          );
        });
      } else {
        return (
          <div className="text-disabled">
            Add some feedback to enable the rubric...
          </div>
        );
      }
    } else {
      return (
        <div className="flex">
          <LinearProgress />
        </div>
      );
    }
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="text-xl font-medium">Annotation Rubric</div>
        <div className="text-disabled">
          Create a custom annotation rubric for your annotators with your own
          criteria, scoring, and descriptions.
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <div className="text-md font-semibold">Instructions</div>
        <div className="text-disabled">
          Write concise, clear, and actionable instructions that focus on the
          key actions reviewers need to take
        </div>
        <Textarea
          placeholder="Write some high-level instructions for your annotators..."
          value={feedbackRubricInstructions ?? ''}
          onChange={(e) => onChangeInstructions(e.target.value)}
          maxRows={10.5}
          minRows={4}
          autoFocus={focusOn === 'instructions'}
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="text-md font-semibold">Feedback</div>
        {filteredFeedbackConfigs && !isEvaluatorMode && (
          <AddFeedbackPopoverWithButton
            defaultOpen={focusOn === 'feedback'}
            onSelect={(value) => {
              append({ feedback_key: value.feedback_key });
            }}
            onCreateConfig={() => {
              feedbackConfigs.mutate();
            }}
            feedbackConfigs={filteredFeedbackConfigs}
          />
        )}
      </div>
      {rubricItemsRender()}
    </div>
  );
};

const RubricItemEditor = ({
  field,
  selectedFeedbackConfig,
  error,
  remove,
  isEvaluatorMode,
}: {
  field: ControllerRenderProps<
    AnnotationQueueFormInput,
    `feedbackRubricItems.${number}`
  >;
  selectedFeedbackConfig: FeedbackConfigSchema;
  error?: string;
  remove: () => void;
  isEvaluatorMode?: boolean;
}) => {
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-secondary p-3">
      <div className="flex flex-col">
        <div className="flex w-full justify-between">
          <div className="flex">
            <FeedbackChip feedback_key={selectedFeedbackConfig.feedback_key} />
          </div>
          {!isEvaluatorMode && (
            <IconButton
              size="sm"
              variant="outlined"
              sx={{
                borderColor: 'var(--gray-100)',
                '&:hover': {
                  borderColor: 'var(--gray-200)',
                },
              }}
              onClick={() => remove()}
            >
              <TrashIcon className="h-4 w-4" />
            </IconButton>
          )}
        </div>

        {error && (
          <FormHelperText className="text-xs text-error">
            {error}
          </FormHelperText>
        )}
      </div>
      <div>
        <span className="text-[13px] font-semibold">Type:</span>
        <span className="text-[13px] font-medium">
          {' '}
          {isEvaluatorMode
            ? 'Boolean'
            : getFeedbackConfigSubtitle(selectedFeedbackConfig, true)}
        </span>
      </div>
      <Input
        placeholder="Write instructions for your annotators on how to assign this feedback..."
        sx={{ ...INPUT_SX, paddingLeft: '1px' }}
        value={field.value.description}
        onChange={(e) =>
          field.onChange({
            ...field.value,
            description: e.target.value,
          })
        }
      />
      {Object.entries(selectedFeedbackConfig?.feedback_config.categories ?? {})
        .length > 0 && (
        <div className="flex flex-col items-stretch gap-1">
          {Array.isArray(selectedFeedbackConfig?.feedback_config.categories) &&
            selectedFeedbackConfig.feedback_config.categories.map(
              (category, categoryIndex) => {
                const categoryName = category.label ?? category.value;
                return (
                  <div
                    key={categoryIndex}
                    className={cn(
                      'flex w-full items-center gap-1 px-2',
                      categoryIndex % 2 === 0
                        ? 'rounded-md bg-brand-tertiary'
                        : ''
                    )}
                  >
                    <div
                      className={cn(
                        'flex items-center rounded px-2 py-1.5 text-xs',
                        categoryIndex % 2 === 0
                          ? 'bg-brand-primary'
                          : 'bg-secondary-hover'
                      )}
                    >
                      {categoryName}
                    </div>
                    <div className="flex-1">
                      <Input
                        placeholder="Describe what this category represents..."
                        sx={{ ...INPUT_SX }}
                        value={
                          field.value.value_descriptions?.[categoryName] ??
                          field.value.score_descriptions?.[categoryName]
                        }
                        onChange={(e) => {
                          const valueKey = category.label
                            ? 'value_descriptions'
                            : 'score_descriptions';
                          field.onChange({
                            ...field.value,
                            [valueKey]: {
                              ...field.value[valueKey],
                              [categoryName]: e.target.value,
                            },
                          });
                        }}
                      />
                    </div>
                  </div>
                );
              }
            )}
        </div>
      )}
    </div>
  );
};
